// src/ui/hooks/useBaseLayoutPropNormalizer.tsx
import {
  ButtonActionComponent,
  FormActionComponent,
  isParameterSelectable,
  isPatternAllowed,
  MultiValueActionComponent,
  SingleValueActionComponent
} from "@dialectlabs/blinks-core";
import { useMemo } from "react";

// src/ui/internal/utils.ts
var confirmLinkTransition = (url) => {
  return window.confirm(
    `This action redirects to another website: ${url}, the link will open in a new tab of your browser`
  );
};

// src/ui/hooks/ui-mappers.ts
var buttonVariantMap = {
  "checking-supportability": "default",
  blocked: "default",
  idle: "default",
  executing: "default",
  success: "success",
  error: "error"
};
var buttonLabelMap = {
  "checking-supportability": "Loading",
  blocked: null,
  idle: null,
  executing: "Executing",
  success: "Completed",
  error: "Failed"
};

// src/ui/hooks/useBaseLayoutPropNormalizer.tsx
var SOFT_LIMIT_BUTTONS = 10;
var SOFT_LIMIT_INPUTS = 3;
var SOFT_LIMIT_FORM_INPUTS = 10;
var useBaseLayoutPropNormalizer = ({
  executeFn,
  executionStatus,
  executingAction,
  blink,
  caption,
  ...props
}) => {
  const buttons = useMemo(
    () => blink?.actions.filter((it) => it instanceof ButtonActionComponent).filter((it) => executingAction ? executingAction === it : true).toSpliced(SOFT_LIMIT_BUTTONS) ?? [],
    [blink, executingAction]
  );
  const inputs = useMemo(
    () => blink?.actions.filter(
      (it) => it instanceof SingleValueActionComponent || it instanceof MultiValueActionComponent
    ).filter((it) => executingAction ? executingAction === it : true).toSpliced(SOFT_LIMIT_INPUTS) ?? [],
    [blink, executingAction]
  );
  const form = useMemo(() => {
    const [formComponent] = blink?.actions.filter((it) => it instanceof FormActionComponent).filter((it) => executingAction ? executingAction === it : true) ?? [];
    return formComponent;
  }, [blink, executingAction]);
  const asButtonProps = (it) => {
    return {
      text: buttonLabelMap[executionStatus] ?? it.label,
      loading: executionStatus === "executing" && (it === executingAction || it.parentComponent === executingAction),
      disabled: blink.disabled || blink.type === "completed" || executionStatus !== "idle",
      variant: buttonVariantMap[blink.type === "completed" ? "success" : executionStatus],
      ctaType: (it.type === "external-link" || it.type === "inline-link") && (executionStatus === "idle" || executionStatus === "blocked") ? "link" : "button",
      onClick: async (params) => {
        const extra = await executeFn(it.parentComponent ?? it, params);
        if (!extra) {
          return;
        }
        if (extra.type === "external-link") {
          const result = confirmLinkTransition(extra.data.externalLink);
          if (result) {
            window.open(
              extra.data.externalLink,
              "_blank",
              "norefferer,noopener"
            );
            return extra.onNext();
          }
          return extra.onCancel?.();
        }
      }
    };
  };
  const asInputProps = (it, { placement } = {
    placement: "standalone"
  }) => {
    return {
      type: it.parameter.type ?? "text",
      placeholder: it.parameter.label,
      disabled: blink.disabled || blink.type === "completed" || executionStatus !== "idle",
      name: it.parameter.name,
      required: it.parameter.required,
      min: it.parameter.min,
      max: it.parameter.max,
      pattern: it instanceof SingleValueActionComponent && isPatternAllowed(it.parameter) ? it.parameter.pattern : void 0,
      options: isParameterSelectable(it.parameter) ? it.parameter.options : void 0,
      description: it.parameter.patternDescription,
      button: placement === "standalone" ? asButtonProps(it.toButtonActionComponent()) : void 0
    };
  };
  const asFormProps = (it) => {
    return {
      button: asButtonProps(it.toButtonActionComponent()),
      inputs: it.parameters.toSpliced(SOFT_LIMIT_FORM_INPUTS).map(
        (parameter) => asInputProps(it.toInputActionComponent(parameter.name), {
          placement: "form"
        })
      )
    };
  };
  const normalizedCaption = useMemo(() => {
    if (!caption) {
      return {};
    }
    if (caption.type === "success") {
      return {
        success: caption.text
      };
    }
    if (caption.type === "error") {
      return {
        error: caption.text
      };
    }
    if (caption.type === "default") {
      return {
        message: caption.text
      };
    }
    return {};
  }, [caption]);
  return {
    ...props,
    title: blink.title,
    description: blink.description,
    image: blink.icon,
    buttons: buttons.map(asButtonProps),
    inputs: inputs.map((i) => asInputProps(i)),
    form: form ? asFormProps(form) : void 0,
    websiteText: (props.websiteText === false ? "" : props.websiteText) ?? props.websiteUrl ?? blink.url,
    shouldShowWebsiteLink: props.websiteText !== false,
    ...normalizedCaption
  };
};

// src/ui/layouts/BaseBlinkLayout.tsx
import {
  DisclaimerType
} from "@dialectlabs/blinks-core";
import clsx9 from "clsx";
import { useState as useState10 } from "react";

// src/ui/internal/Badge.tsx
import clsx from "clsx";
import { jsx, jsxs } from "react/jsx-runtime";
var variantClasses = {
  error: {
    container: "group bg-transparent-error",
    text: "text-text-error group-hover:text-text-error-hover",
    icon: "text-icon-error group-hover:text-icon-error-hover"
  },
  warning: {
    container: "group bg-transparent-warning",
    text: "text-text-warning group-hover:text-text-warning-hover",
    icon: "text-icon-warning group-hover:text-icon-warning-hover"
  },
  default: {
    container: "group bg-transparent-grey",
    text: "text-text-primary group-hover:text-text-primary-hover",
    icon: "text-icon-primary group-hover:text-icon-primary-hover"
  }
};
var Badge = ({
  variant = "default",
  children,
  className,
  icon
}) => {
  const { container, icon: iconClassNames, text } = variantClasses[variant];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx(
        container,
        "text-subtext inline-flex items-center justify-center gap-1 rounded-full font-semibold leading-none",
        className,
        {
          "aspect-square p-1": !children && icon,
          "px-1.5 py-1": children
        }
      ),
      children: [
        children && /* @__PURE__ */ jsx(
          "span",
          {
            className: clsx(
              text,
              "mt-0.5 transition-colors motion-reduce:transition-none"
            ),
            children
          }
        ),
        icon && /* @__PURE__ */ jsx(
          "div",
          {
            className: clsx(
              iconClassNames,
              "transition-colors motion-reduce:transition-none"
            ),
            children: icon
          }
        )
      ]
    }
  );
};

// src/ui/internal/SimpleMarkdown.tsx
import Markdown, { RuleType, sanitizer } from "markdown-to-jsx";
import { useId } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var LinkWithConfirm = ({
  href,
  title,
  children
}) => {
  return /* @__PURE__ */ jsx2(
    "a",
    {
      href,
      className: "text-text-link hover:text-text-link-hover underline transition-colors motion-reduce:transition-none",
      target: "_blank",
      title,
      onClick: (e) => {
        if (!confirmLinkTransition(href)) {
          e.preventDefault();
        }
      },
      rel: "noopener noreferrer",
      children
    }
  );
};
var SimpleMarkdown = ({ text }) => {
  const id = useId();
  return /* @__PURE__ */ jsx2(
    Markdown,
    {
      options: {
        disableParsingRawHTML: true,
        slugify: (str) => `${str}-${id}`,
        // @ts-expect-error - return type ReactChild is deprecated, assuming ReactNode
        renderRule(next, node, renderChildren, state) {
          switch (node.type) {
            case RuleType.image:
              return null;
            case RuleType.gfmTask:
              return /* @__PURE__ */ jsx2("span", { className: "mb-[0.125em] inline-block", children: node.completed ? "\u2705" : "\u2B1C" });
            case RuleType.link:
              const sanitizedHref = sanitizer(node.target);
              if (sanitizedHref) {
                return /* @__PURE__ */ jsx2(
                  LinkWithConfirm,
                  {
                    href: sanitizedHref,
                    title: node.title,
                    children: renderChildren(node.children, state)
                  },
                  state.key
                );
              }
              return /* @__PURE__ */ jsx2("span", { children: renderChildren(node.children, state) }, state.key);
            case RuleType.heading:
            case RuleType.paragraph:
              return /* @__PURE__ */ jsx2("p", { className: "mb-[0.35em] last:mb-0", children: renderChildren(node.children, state) }, state.key);
            case RuleType.blockQuote:
              return /* @__PURE__ */ jsx2("blockquote", { className: "mb-[0.35em] last:mb-0", children: renderChildren(node.children, state) }, state.key);
            case RuleType.codeBlock:
              return /* @__PURE__ */ jsx2("pre", { className: "text-wrap", children: /* @__PURE__ */ jsx2("code", { children: node.text }) }, state.key);
            case RuleType.orderedList:
              return /* @__PURE__ */ jsx2(
                "ol",
                {
                  className: "list-inside list-decimal [li>&]:ps-4",
                  children: node.items.map((item, index) => /* @__PURE__ */ jsx2("li", { children: renderChildren(item, state) }, index))
                },
                state.key
              );
            case RuleType.unorderedList:
              return /* @__PURE__ */ jsx2(
                "ul",
                {
                  className: "list-inside list-disc [li>&]:ps-4",
                  children: node.items.map((item, index) => /* @__PURE__ */ jsx2("li", { children: renderChildren(item, state) }, index))
                },
                state.key
              );
            case RuleType.breakThematic:
              return /* @__PURE__ */ jsx2("hr", { className: "my-[0.5em]" }, state.key);
            default:
              return next();
          }
        }
      },
      children: text
    }
  );
};

// src/ui/internal/Snackbar.tsx
import clsx2 from "clsx";
import { jsx as jsx3 } from "react/jsx-runtime";
var variantClasses2 = {
  error: "bg-transparent-error text-text-error border-stroke-error",
  warning: "bg-transparent-warning text-text-warning border-stroke-warning"
};
var Snackbar = ({ variant = "warning", children }) => {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      className: clsx2(
        variantClasses2[variant],
        "rounded-lg border p-3 text-subtext"
      ),
      children
    }
  );
};

// src/ui/internal/icons/CheckIcon.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var CheckIcon = (props) => /* @__PURE__ */ jsx4(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 17,
    height: 16,
    viewBox: "0 0 17 16",
    fill: "none",
    ...props,
    children: /* @__PURE__ */ jsx4(
      "path",
      {
        d: "M15.348 3.509a.984.984 0 010 1.437l-8.173 8.172a.984.984 0 01-1.437 0L1.652 9.032a.984.984 0 010-1.437.984.984 0 011.437 0l3.384 3.353 7.438-7.439a.984.984 0 011.437 0z",
        fill: "currentColor"
      }
    )
  }
);

// src/ui/internal/icons/ConfigIcon.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var ConfigIcon = (props) => /* @__PURE__ */ jsxs2(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: [
      /* @__PURE__ */ jsx5(
        "path",
        {
          fill: "currentColor",
          d: "M7.037 8.108a2.65 2.65 0 1 1 1.07-1.071 1.522 1.522 0 0 1 1.553.094 1.523 1.523 0 0 1 1.498-1.239h.398v-.49a.666.666 0 0 0-.55-.653l-.866-.151a4.488 4.488 0 0 0-.447-1.078l.505-.72a.666.666 0 0 0-.076-.84l-.519-.528a.666.666 0 0 0-.852-.074l-.72.505a4.482 4.482 0 0 0-1.078-.447L6.802.55A.666.666 0 0 0 6.149 0h-.735a.666.666 0 0 0-.653.55l-.152.866a4.48 4.48 0 0 0-1.078.447l-.726-.505a.666.666 0 0 0-.846.074l-.527.527a.666.666 0 0 0-.074.854l.505.72c-.195.338-.346.7-.447 1.078l-.866.151a.666.666 0 0 0-.55.654v.734a.666.666 0 0 0 .55.653l.866.152c.101.377.251.74.447 1.078l-.505.716a.666.666 0 0 0 .074.854l.527.52a.666.666 0 0 0 .854.075l.72-.505c.338.195.7.346 1.078.447l.151.866a.666.666 0 0 0 .654.55h.49v-.398A1.523 1.523 0 0 1 7.143 9.66a1.522 1.522 0 0 1-.107-1.552ZM16 11.158v.578a.524.524 0 0 1-.434.509l-.683.12a3.53 3.53 0 0 1-.352.86l.398.568a.524.524 0 0 1-.059.672l-.41.41a.524.524 0 0 1-.672.059l-.563-.403a3.537 3.537 0 0 1-.85.352l-.13.683a.524.524 0 0 1-.51.434h-.577a.524.524 0 0 1-.518-.434l-.12-.683a3.536 3.536 0 0 1-.848-.352l-.568.398a.525.525 0 0 1-.673-.059l-.41-.41a.524.524 0 0 1-.059-.672l.4-.563a3.535 3.535 0 0 1-.352-.85l-.683-.13a.524.524 0 0 1-.434-.518v-.57a.524.524 0 0 1 .434-.517l.683-.12a3.54 3.54 0 0 1 .352-.848l-.398-.567a.524.524 0 0 1 .059-.672l.41-.41a.524.524 0 0 1 .672-.059l.567.398c.266-.154.552-.272.849-.352l.119-.683a.524.524 0 0 1 .518-.434h.578a.524.524 0 0 1 .509.434l.12.683c.3.079.59.197.86.352l.566-.398a.524.524 0 0 1 .673.059l.41.41a.524.524 0 0 1 .058.672l-.401.567c.154.266.272.552.352.849l.683.119a.524.524 0 0 1 .434.518Zm-2.991.289a1.563 1.563 0 1 0-3.126 0 1.563 1.563 0 0 0 3.126 0Z"
        }
      ),
      /* @__PURE__ */ jsx5(
        "path",
        {
          fill: "currentColor",
          d: "M6.886 5.777a1.105 1.105 0 1 1-2.21 0 1.105 1.105 0 0 1 2.21 0Z"
        }
      )
    ]
  }
);

// src/ui/internal/icons/DeepLinkIcon.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var DeepLinkIcon = (props) => /* @__PURE__ */ jsxs3(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 10,
    height: 10,
    fill: "none",
    viewBox: "0 0 10 10",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: [
      /* @__PURE__ */ jsx6(
        "path",
        {
          fill: "currentColor",
          d: "M2.5 1h1.793v1H2.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5V5.707h1V7.5A1.5 1.5 0 0 1 7.5 9h-5A1.5 1.5 0 0 1 1 7.5v-5A1.5 1.5 0 0 1 2.5 1Z"
        }
      ),
      /* @__PURE__ */ jsx6(
        "path",
        {
          fill: "currentColor",
          d: "M9 4H8V2.708l-3 3L4.293 5l3-3H6V1h2.5a.5.5 0 0 1 .5.5V4Z"
        }
      )
    ]
  }
);

// src/ui/internal/icons/DialectLogo.tsx
import { jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var DialectLogo = (props) => /* @__PURE__ */ jsxs4(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 63,
    height: 12,
    viewBox: "0 0 63 12",
    fill: "none",
    ...props,
    children: [
      /* @__PURE__ */ jsx7(
        "path",
        {
          fill: "currentColor",
          d: "M1.26 8.696C.911 7.467.957 5.198.957 5.198s1.27 2.048 3.459 2.688c1.613.472 3.833.508 5.23-.123 1.692-.555 2.742-2.281 2.246-3.515-.728-1.814-2.846-2.532-5.055-2.36a6.541 6.541 0 0 0-3.922 1.741c-.826.79-1.034 1.425-1.034 1.425s-.644-.687-.722-1.425C.98 1.936 2.502.617 4.672.161c2.939-.618 6.391.582 8.276 2.93 1.303 1.623 1.665 3.824.588 5.72-1.613 2.837-5.205 3.399-7.53 3.128-2.327-.27-4.354-1.851-4.747-3.243ZM27.733 11.268v-7.72h2.433v7.72h-2.433ZM28.956 2.647a1.24 1.24 0 0 1-.88-.34 1.103 1.103 0 0 1-.368-.837c0-.32.123-.595.368-.823.246-.229.537-.347.88-.347.343 0 .64.118.88.347.245.228.368.503.368.823 0 .327-.123.602-.368.837a1.24 1.24 0 0 1-.88.34Z"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          fill: "currentColor",
          fillRule: "evenodd",
          d: "M22.063 11.268H18.3V.974h3.758c1.035 0 1.934.209 2.678.62.75.412 1.332 1 1.74 1.772.407.764.614 1.686.614 2.751 0 1.072-.207 1.994-.614 2.765a4.218 4.218 0 0 1-1.734 1.772c-.75.405-1.643.614-2.678.614Zm-1.3-2.124h1.21c.569 0 1.054-.098 1.449-.294.4-.196.698-.517.899-.961.207-.445.31-1.04.31-1.778 0-.739-.103-1.327-.31-1.771-.207-.445-.511-.759-.912-.955-.401-.196-.886-.294-1.475-.294h-1.17v6.053ZM31.86 11.15a3.23 3.23 0 0 0 1.3.249c.356 0 .673-.046.958-.138a2.22 2.22 0 0 0 .743-.405c.214-.183.388-.405.524-.66h.058v1.079h2.29V6.018a2.3 2.3 0 0 0-.252-1.091 2.26 2.26 0 0 0-.705-.804 3.404 3.404 0 0 0-1.074-.503 4.894 4.894 0 0 0-1.345-.177c-.686 0-1.268.105-1.753.32-.485.21-.867.504-1.145.876a2.626 2.626 0 0 0-.51 1.255l2.244.079a.897.897 0 0 1 .375-.582c.194-.144.453-.21.776-.21.297 0 .537.073.711.21.19.156.266.379.266.62a.418.418 0 0 1-.182.367c-.116.085-.31.15-.575.196a13.93 13.93 0 0 1-1.048.124c-.388.033-.75.098-1.093.196-.343.092-.64.236-.906.419a1.885 1.885 0 0 0-.614.719c-.149.294-.226.653-.226 1.085 0 .51.11.934.323 1.274.188.34.485.588.86.758Zm2.755-1.523a1.557 1.557 0 0 1-.705.164c-.278 0-.51-.066-.692-.203-.18-.137-.271-.327-.271-.582a.72.72 0 0 1 .123-.43.906.906 0 0 1 .368-.302c.162-.078.356-.137.589-.17.11-.02.233-.032.356-.052.123-.02.239-.046.355-.072a2.59 2.59 0 0 0 .57-.183v.732c0 .249-.065.47-.188.66a1.275 1.275 0 0 1-.505.438Z",
          clipRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          fill: "currentColor",
          d: "M40.844.974v10.294h-2.432V.974h2.432ZM51.283 10.909c.576.333 1.268.503 2.076.503.718 0 1.333-.13 1.857-.392.524-.268.931-.634 1.216-1.112.29-.477.446-1.032.472-1.666h-2.27a1.779 1.779 0 0 1-.214.693 1.13 1.13 0 0 1-.433.424 1.182 1.182 0 0 1-.595.144c-.285 0-.53-.078-.744-.235-.214-.163-.375-.399-.498-.706-.117-.314-.175-.7-.175-1.157 0-.457.058-.837.175-1.144.116-.313.285-.549.498-.706.213-.163.46-.241.744-.241.349 0 .627.11.847.34.22.222.35.535.401.928h2.27c-.019-.634-.174-1.183-.472-1.654a2.985 2.985 0 0 0-1.235-1.098c-.524-.255-1.139-.386-1.844-.386-.802 0-1.487.17-2.063.503a3.373 3.373 0 0 0-1.326 1.406c-.31.595-.466 1.287-.466 2.078 0 .785.156 1.484.466 2.079a3.25 3.25 0 0 0 1.313 1.399Z"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          fill: "currentColor",
          fillRule: "evenodd",
          d: "M45.285 11.412c-.801 0-1.487-.157-2.07-.477a3.269 3.269 0 0 1-1.332-1.373c-.31-.595-.465-1.307-.465-2.124 0-.798.155-1.497.465-2.092.31-.601.75-1.065 1.32-1.399.569-.333 1.235-.503 2.005-.503a4.22 4.22 0 0 1 1.494.255c.453.17.84.425 1.17.758.33.334.583.752.764 1.242.181.49.272 1.053.272 1.687V8h-5.09v.065c0 .314.058.589.18.83.123.236.298.419.524.55.227.13.492.195.809.195.213 0 .414-.032.588-.091.175-.059.324-.15.453-.268.123-.118.22-.261.285-.431l2.231.065c-.09.503-.297.948-.614 1.32-.31.373-.718.66-1.23.87-.51.202-1.092.307-1.758.307Zm1.365-4.85c-.006-.261-.045-.464-.161-.667a1.356 1.356 0 0 0-.511-.503 1.403 1.403 0 0 0-.712-.177 1.43 1.43 0 0 0-.73.184 1.339 1.339 0 0 0-.512.496 1.436 1.436 0 0 0-.2.667h2.826Z",
          clipRule: "evenodd"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          fill: "currentColor",
          d: "M61.901 3.549v1.81h-1.377v3.484c0 .15.026.275.07.366a.444.444 0 0 0 .214.196c.09.04.207.052.337.052.09 0 .187-.006.29-.026.104-.02.188-.039.24-.052l.369 1.771c-.117.033-.278.079-.492.124a3.82 3.82 0 0 1-.75.099c-.57.026-1.06-.04-1.468-.203a1.907 1.907 0 0 1-.932-.765c-.213-.346-.317-.778-.31-1.3V5.353h-1.01V3.549h1.01v-1.85h2.432v1.85H61.9Z"
        }
      )
    ]
  }
);

// src/ui/internal/icons/ExclamationShieldIcon.tsx
import "react";
import { jsx as jsx8 } from "react/jsx-runtime";
var ExclamationShieldIcon = (props) => /* @__PURE__ */ jsx8(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: /* @__PURE__ */ jsx8(
      "path",
      {
        fill: "currentColor",
        fillRule: "evenodd",
        d: "m8.38 1.087 5.483 2.334c.641.291 1.137.904 1.137 1.662-.03 2.916-1.225 8.195-6.241 10.616a1.794 1.794 0 0 1-1.546 0c-5.016-2.42-6.212-7.7-6.212-10.616-.029-.758.467-1.37 1.108-1.662l5.483-2.334a.932.932 0 0 1 .788 0Zm-1.313 10.29c0 .524.408.933.933.933a.94.94 0 0 0 .933-.934.96.96 0 0 0-.933-.933.94.94 0 0 0-.933.934ZM8 4.109a.9.9 0 0 0-.9.9v3.606a.9.9 0 0 0 1.8 0V5.01a.9.9 0 0 0-.9-.9Z",
        clipRule: "evenodd"
      }
    )
  }
);

// src/ui/internal/icons/InfoShieldIcon.tsx
import "react";
import { jsx as jsx9 } from "react/jsx-runtime";
var InfoShieldIcon = (props) => /* @__PURE__ */ jsx9(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: /* @__PURE__ */ jsx9(
      "path",
      {
        fill: "currentColor",
        fillRule: "evenodd",
        d: "M13.863 3.42 8.38 1.088a.932.932 0 0 0-.787 0L2.108 3.421c-.641.291-1.137.904-1.108 1.662 0 2.916 1.196 8.195 6.212 10.616.496.233 1.05.233 1.546 0 5.016-2.42 6.212-7.7 6.241-10.616 0-.758-.496-1.37-1.137-1.662Zm-6.33 7.35h-.582a.69.69 0 0 0-.7.7c0 .408.292.7.7.7h2.216c.379 0 .7-.292.7-.7 0-.38-.321-.7-.7-.7h-.234V8.204c0-.38-.32-.7-.7-.7H7.208a.69.69 0 0 0-.7.7c0 .408.292.7.7.7h.326v1.866Zm-.466-5.133c0 .525.408.933.933.933a.94.94 0 0 0 .933-.933A.96.96 0 0 0 8 4.704a.94.94 0 0 0-.933.933Z",
        clipRule: "evenodd"
      }
    )
  }
);

// src/ui/internal/icons/LinkIcon.tsx
import "react";
import { jsx as jsx10, jsxs as jsxs5 } from "react/jsx-runtime";
var LinkIcon = (props) => /* @__PURE__ */ jsxs5(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: [
      /* @__PURE__ */ jsxs5("g", { fill: "currentColor", clipPath: "url(#a)", children: [
        /* @__PURE__ */ jsx10("path", { d: "M7.409 9.774 9.774 7.41a.836.836 0 1 0-1.183-1.183L6.226 8.592A.836.836 0 1 0 7.41 9.774Z" }),
        /* @__PURE__ */ jsx10("path", { d: "M10.76.503A4.709 4.709 0 0 0 7.41 1.889L5.83 3.467A.836.836 0 1 0 7.014 4.65L8.59 3.072a3.067 3.067 0 0 1 4.338 4.337L11.35 8.987a.835.835 0 1 0 1.182 1.182l1.578-1.577a4.738 4.738 0 0 0-3.35-8.09ZM5.24 15.497a4.706 4.706 0 0 0 3.351-1.386l1.578-1.577a.836.836 0 1 0-1.182-1.183l-1.578 1.578a3.067 3.067 0 1 1-4.337-4.337L4.65 7.014A.836.836 0 1 0 3.467 5.83L1.889 7.41a4.737 4.737 0 0 0 3.351 8.088Z" })
      ] }),
      /* @__PURE__ */ jsx10("defs", { children: /* @__PURE__ */ jsx10("clipPath", { id: "a", children: /* @__PURE__ */ jsx10("path", { fill: "#fff", d: "M0 0h16v16H0z" }) }) })
    ]
  }
);

// src/ui/internal/icons/SpinnerDots.tsx
import { useMemo as useMemo2 } from "react";
import { Fragment, jsx as jsx11, jsxs as jsxs6 } from "react/jsx-runtime";
var POINTS = [0, 45, 90, 135, 180, 225, 270, 315];
var SpinnerDots = ({
  width = 16
}) => {
  const dots = useMemo2(
    () => POINTS.map((point, index) => /* @__PURE__ */ jsx11(
      "circle",
      {
        cy: 12,
        cx: 12,
        r: 4,
        transform: `rotate(${point}, 24, 24)`,
        fillOpacity: index < 2 ? 0 : 1
      },
      point
    )),
    []
  );
  return /* @__PURE__ */ jsxs6(Fragment, { children: [
    /* @__PURE__ */ jsx11("style", { children: "@keyframes spin{to{transform:rotate(360deg)}}" }),
    /* @__PURE__ */ jsx11(
      "svg",
      {
        viewBox: "0 0 48 48",
        width,
        fill: "currentColor",
        "data-testid": "rotating-dots-svg",
        "aria-label": "rotating-dots-loading",
        style: {
          animationDuration: "1s",
          animation: "spin 1s steps(8, end) infinite"
        },
        children: dots
      }
    )
  ] });
};

// src/ui/internal/Button.tsx
import clsx3 from "clsx";
import { jsx as jsx12, jsxs as jsxs7 } from "react/jsx-runtime";
var Button = ({
  onClick,
  disabled,
  variant = "default",
  children,
  ctaType = "button",
  className
}) => {
  const isLink = ctaType === "link";
  return /* @__PURE__ */ jsxs7(
    "button",
    {
      className: clsx3(
        "rounded-button text-text h-input-height relative flex w-full items-center justify-center text-nowrap px-4 py-3 font-semibold transition-colors motion-reduce:transition-none",
        {
          "px-5": isLink,
          "bg-button-disabled text-text-button-disabled": disabled && variant !== "success",
          "bg-button text-text-button hover:bg-button-hover": !disabled && variant !== "success",
          "bg-button-success text-text-button-success": variant === "success"
          // success is likely to be always disabled
        },
        className
      ),
      disabled,
      onClick,
      children: [
        children,
        isLink && /* @__PURE__ */ jsx12("span", { className: "absolute right-2 top-2", children: /* @__PURE__ */ jsx12(
          DeepLinkIcon,
          {
            className: clsx3("h-2.5 w-2.5", {
              "text-text-button-disabled": disabled && variant !== "success",
              "text-text-button": !disabled && variant !== "success",
              "text-text-button-success": variant === "success"
              // success is likely to be always disabled
            })
          }
        ) })
      ]
    }
  );
};

// src/ui/internal/inputs/ActionButton.tsx
import { jsx as jsx13, jsxs as jsxs8 } from "react/jsx-runtime";
var ActionButton = ({
  text,
  loading,
  disabled,
  variant,
  onClick,
  ctaType
}) => {
  const ButtonContent = () => {
    if (loading)
      return /* @__PURE__ */ jsxs8("span", { className: "flex flex-row items-center justify-center gap-2 text-nowrap", children: [
        text,
        " ",
        /* @__PURE__ */ jsx13(SpinnerDots, {})
      ] });
    if (variant === "success")
      return /* @__PURE__ */ jsxs8("span", { className: "flex flex-row items-center justify-center gap-2 text-nowrap", children: [
        text,
        /* @__PURE__ */ jsx13(CheckIcon, {})
      ] });
    return text;
  };
  return /* @__PURE__ */ jsx13(
    Button,
    {
      onClick: () => onClick(),
      disabled,
      variant,
      ctaType,
      className: "blink-action-button",
      children: /* @__PURE__ */ jsx13("span", { className: "min-w-0 truncate", children: /* @__PURE__ */ jsx13(ButtonContent, {}) })
    }
  );
};

// src/ui/internal/inputs/ActionCheckboxGroup.tsx
import clsx5 from "clsx";
import { useEffect, useMemo as useMemo3, useState } from "react";

// src/ui/internal/Checkbox.tsx
import clsx4 from "clsx";
import { useId as useId2 } from "react";

// src/ui/internal/icons/CheckboxCheckIcon.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var SvgComponent = (props) => /* @__PURE__ */ jsx14(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: /* @__PURE__ */ jsx14(
      "path",
      {
        fill: "currentColor",
        fillRule: "evenodd",
        d: "M12.6 5.398a.85.85 0 0 1-.001 1.202l-4.784 4.773a.85.85 0 0 1-1.193.008L3.934 8.77a.85.85 0 0 1 1.184-1.22l2.088 2.027 4.192-4.182a.85.85 0 0 1 1.202.002Z",
        clipRule: "evenodd"
      }
    )
  }
);
var CheckboxCheckIcon_default = SvgComponent;

// src/ui/internal/Checkbox.tsx
import { jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var Checkbox = ({
  label,
  value,
  onChange,
  name,
  disabled,
  inputValue
}) => {
  const id = useId2();
  const labelId = `${id}_label`;
  return /* @__PURE__ */ jsxs9(
    "button",
    {
      className: clsx4("flex h-full gap-2.5", {
        "cursor-pointer": !disabled,
        "cursor-not-allowed": disabled
      }),
      onClick: () => !disabled && onChange(!value),
      children: [
        /* @__PURE__ */ jsxs9("div", { className: "flex h-full items-center", children: [
          /* @__PURE__ */ jsx15(
            "input",
            {
              type: "checkbox",
              name,
              className: "hidden",
              defaultValue: inputValue
            }
          ),
          /* @__PURE__ */ jsx15(
            "span",
            {
              role: "checkbox",
              id,
              "aria-labelledby": labelId,
              className: clsx4(
                "mt-0.5 flex aspect-square h-[16px] items-center justify-center rounded-lg border transition-colors motion-reduce:transition-none",
                {
                  "border-input-stroke bg-input-bg": !value && !disabled,
                  "border-input-stroke-selected bg-input-bg-selected": value && !disabled,
                  "border-input-stroke-disabled bg-input-bg": !value && disabled,
                  "border-input-stroke-disabled bg-input-bg-disabled": value && disabled
                }
              ),
              children: /* @__PURE__ */ jsx15(
                CheckboxCheckIcon_default,
                {
                  className: clsx4("h-full w-full text-input-bg", {
                    block: value,
                    hidden: !value
                  })
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx15(
          "label",
          {
            className: "cursor-[inherit] text-text text-text-input",
            id: labelId,
            children: label
          }
        )
      ]
    }
  );
};

// src/ui/internal/inputs/utils.ts
var buildDefaultTextDescription = ({
  min,
  max
}) => {
  if (min && max) return `Type between ${min} and ${max} characters`;
  if (min) return `Type minimum ${min} characters`;
  if (max) return `Type maximum ${max} characters`;
  return null;
};
var buildDefaultNumberDescription = ({
  min,
  max
}) => {
  if (min && max) return `Enter a number between ${min} and ${max}`;
  if (min) return `Enter a number greater than ${min}`;
  if (max) return `Enter a number less than ${max}`;
  return null;
};
var buildDefaultDateDescription = ({
  min,
  max,
  includeTime
}) => {
  const minDate = min ? new Date(min) : null;
  const maxDate = max ? new Date(max) : null;
  const formatter = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: includeTime ? "numeric" : void 0,
    minute: includeTime ? "numeric" : void 0
  });
  if (minDate && maxDate)
    return `Pick a date between ${formatter.format(minDate)} and ${formatter.format(maxDate)}`;
  if (minDate) return `Pick a date after ${formatter.format(minDate)}`;
  if (maxDate) return `Pick a date before ${formatter.format(maxDate)}`;
  return null;
};
var buildDefaultCheckboxGroupDescription = ({
  min,
  max
}) => {
  if (min && max) return `Select between ${min} and ${max} options`;
  if (min) return `Select minimum ${min} options`;
  if (max) return `Select maximum ${max} options`;
  return null;
};

// src/ui/internal/inputs/ActionCheckboxGroup.tsx
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var validate = (values, { required, min, max }) => {
  if (required && !values.length) {
    return false;
  }
  if (min && values.length < min) {
    return false;
  }
  if (max && values.length > max) {
    return false;
  }
  return true;
};
var normalizeValue = (value) => {
  return Object.entries(value).filter(([, v]) => v).map(([k]) => k);
};
var ActionCheckboxGroup = ({
  placeholder: label,
  // in base inputs it's placeholder, for selectables - label
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  min,
  max,
  description,
  options = [],
  required
}) => {
  const minChoices = min;
  const maxChoices = max;
  const isStandalone = !!button;
  const finalDescription = description || buildDefaultCheckboxGroupDescription({
    min: minChoices,
    max: maxChoices
  });
  const hasInitiallySelectedOption = useMemo3(
    () => options.find((option) => option.selected),
    [options]
  );
  const [state, setState] = useState({
    value: Object.fromEntries(
      options.map((option) => [option.value, option.selected ?? false])
    ),
    valid: isStandalone ? !!hasInitiallySelectedOption : !(required && !hasInitiallySelectedOption)
  });
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    onValidityChange?.(state.valid);
  }, []);
  const extendedChange = (name2, value) => {
    setState((prev) => {
      const newValue = { ...prev.value, [name2]: value };
      const normalizedValue2 = normalizeValue(newValue);
      onChange?.(normalizedValue2);
      const validity = validate(normalizedValue2, {
        required: isStandalone,
        min: minChoices,
        max: maxChoices
      });
      onValidityChange?.(validity);
      return {
        value: newValue,
        valid: validity
      };
    });
    setTouched(true);
  };
  const normalizedValue = useMemo3(
    () => normalizeValue(state.value),
    [state.value]
  );
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: clsx5("blink-input py-1.5", {
        "bg-bg-secondary rounded-input px-1.5 pt-2": isStandalone
      }),
      children: [
        /* @__PURE__ */ jsxs10("div", { className: clsx5(isStandalone && "px-2"), children: [
          label && /* @__PURE__ */ jsx16("div", { className: "blink-input-label mb-1", children: /* @__PURE__ */ jsxs10("label", { className: "text-subtext text-text-input block font-semibold", children: [
            label,
            required && !isStandalone ? "*" : ""
          ] }) }),
          /* @__PURE__ */ jsx16(
            "div",
            {
              className: clsx5("pt-2", {
                "flex flex-col gap-3": !isStandalone,
                "grid grid-cols-2 gap-x-4 gap-y-5": isStandalone
              }),
              children: options.map((option) => /* @__PURE__ */ jsx16(
                "div",
                {
                  className: "inline-flex",
                  children: /* @__PURE__ */ jsx16(
                    Checkbox,
                    {
                      label: option.label,
                      value: state.value[option.value],
                      inputValue: option.value,
                      onChange: (value) => extendedChange(option.value, value),
                      name,
                      disabled
                    }
                  )
                },
                `${option.value}_${option.label}`
              ))
            }
          )
        ] }),
        isStandalone && /* @__PURE__ */ jsx16("div", { className: "mt-4", children: /* @__PURE__ */ jsx16(
          ActionButton,
          {
            ...button,
            onClick: () => button.onClick({ [name]: normalizedValue }),
            disabled: button.disabled || !normalizedValue.length || !state.valid
          }
        ) }),
        finalDescription && /* @__PURE__ */ jsx16(
          "div",
          {
            className: clsx5(
              "blink-input-description text-caption",
              touched && !state.valid ? "text-text-error" : "text-text-secondary",
              isStandalone ? "mb-2 mt-2.5 px-2" : "mt-3"
            ),
            children: /* @__PURE__ */ jsx16(SimpleMarkdown, { text: finalDescription })
          }
        )
      ]
    }
  );
};

// src/ui/internal/inputs/ActionDateInput.tsx
import {
  useCallback,
  useEffect as useEffect2,
  useId as useId3,
  useMemo as useMemo4,
  useState as useState2
} from "react";

// src/ui/internal/icons/CalendarIcon.tsx
import { jsx as jsx17, jsxs as jsxs11 } from "react/jsx-runtime";
var SvgComponent2 = (props) => /* @__PURE__ */ jsxs11(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: [
      /* @__PURE__ */ jsx17("g", { clipPath: "url(#a)", children: /* @__PURE__ */ jsx17(
        "path",
        {
          fill: "currentColor",
          fillRule: "evenodd",
          d: "M5.805 1.232a.732.732 0 1 0-1.464 0v.37c-.362.006-.685.019-.969.048-.526.055-1.004.174-1.428.468-.29.2-.54.451-.741.74-.294.425-.412.902-.467 1.429-.049.462-.053 1.026-.053 1.7V10.43c0 .99 0 1.808.087 2.454.091.68.29 1.279.77 1.759.48.48 1.08.679 1.759.77.646.087 1.463.087 2.454.087h4.494c.99 0 1.808 0 2.454-.087.68-.091 1.279-.29 1.759-.77.48-.48.679-1.08.77-1.759.087-.646.087-1.463.087-2.454V5.988c0-.675-.004-1.239-.053-1.701-.055-.527-.173-1.004-.467-1.428-.2-.29-.452-.54-.741-.741-.425-.294-.902-.413-1.428-.468-.284-.03-.607-.042-.97-.048v-.37a.732.732 0 0 0-1.463 0v.366h-4.39v-.366Zm8.049 4.756c-.001-.685-.006-1.17-.045-1.55-.043-.406-.12-.607-.215-.746-.1-.145-.226-.27-.371-.37-.14-.097-.34-.174-.747-.216a9.532 9.532 0 0 0-.818-.04v.36a.732.732 0 0 1-1.463 0v-.365h-4.39v.366a.732.732 0 1 1-1.464 0v-.361c-.324.005-.59.016-.817.04-.407.042-.608.119-.747.215-.145.1-.27.226-.37.37-.097.14-.173.34-.216.748-.04.379-.044.864-.045 1.549h11.707ZM2.146 7.45v2.927c0 1.056.002 1.774.074 2.311.07.517.192.756.355.919.163.163.402.285.919.355.537.072 1.255.074 2.31.074h4.391c1.056 0 1.774-.002 2.311-.074.517-.07.756-.192.919-.355.163-.163.285-.402.355-.919.072-.537.074-1.255.074-2.311V7.451H2.146Z",
          clipRule: "evenodd"
        }
      ) }),
      /* @__PURE__ */ jsx17("defs", { children: /* @__PURE__ */ jsx17("clipPath", { id: "a", children: /* @__PURE__ */ jsx17("path", { fill: "#fff", d: "M0 0h16v16H0z" }) }) })
    ]
  }
);
var CalendarIcon_default = SvgComponent2;

// src/ui/internal/inputs/BaseInputContainer.tsx
import clsx6 from "clsx";
import {
  cloneElement
} from "react";
import { jsx as jsx18, jsxs as jsxs12 } from "react/jsx-runtime";
var BaseInputContainer = ({
  children,
  leftAdornment,
  rightAdornment,
  footer,
  description,
  standalone = true,
  className
}) => {
  return /* @__PURE__ */ jsxs12("div", { children: [
    /* @__PURE__ */ jsxs12(
      "div",
      {
        className: clsx6(
          "blink-input border-input-stroke min-h-input-height peer relative box-border flex flex-wrap items-center gap-1.5 gap-y-2 border px-1.5 transition-colors motion-reduce:transition-none",
          // focus, invalid, required
          "focus-within:has-[:invalid]:border-input-stroke-error focus-within:has-[:valid]:border-input-stroke-selected focus-within:hover:has-[:invalid]:border-input-stroke-error focus-within:hover:has-[:valid]:border-input-stroke-selected",
          // enabled,
          "hover:has-[:enabled]:border-input-stroke-hover",
          { "py-1.5": !!rightAdornment },
          standalone ? "rounded-input-standalone" : "rounded-input",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs12("div", { className: "flex min-w-0 flex-[10] basis-1/2 items-center gap-1.5 pl-2.5", children: [
            leftAdornment && /* @__PURE__ */ jsx18("div", { className: "blink-input-left-adornment", children: leftAdornment }),
            cloneElement(children, {
              className: clsx6(
                "blink-input-inner bg-input-bg text-text-input placeholder:text-text-input-placeholder disabled:text-text-input-disabled min-h-7 min-w-0 flex-1 truncate outline-none",
                children.props.className
              )
            })
          ] }),
          rightAdornment && /* @__PURE__ */ jsx18("div", { className: "blink-input-right-adornment max-w-full flex-1 whitespace-nowrap", children: rightAdornment })
        ]
      }
    ),
    footer && /* @__PURE__ */ jsx18("div", { className: "blink-input-footer mt-2", children: footer }),
    description && /* @__PURE__ */ jsx18("div", { className: "blink-input-description text-caption text-text-secondary peer-[:focus-within:has(:invalid)]:text-text-error mt-1.5 font-medium", children: /* @__PURE__ */ jsx18(SimpleMarkdown, { text: description }) })
  ] });
};

// src/ui/internal/inputs/ActionDateInput.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var ActionDateInput = ({
  type = "date",
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  pattern,
  min,
  max,
  description,
  required
}) => {
  const id = useId3();
  const isStandalone = !!button;
  const [value, setValue] = useState2("");
  const [isValid, setValid] = useState2(!isStandalone && !required);
  const [touched, setTouched] = useState2(false);
  const minDate = min;
  const maxDate = max;
  useEffect2(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = useCallback(
    (e) => {
      const value2 = e.currentTarget.value;
      const validity = e.currentTarget.checkValidity();
      setValue(value2);
      setValid(validity);
      onChange?.(value2);
      onValidityChange?.(validity);
    },
    [onChange, onValidityChange]
  );
  const placeholderWithRequired = (placeholder || "Enter a date") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo4(
    () => ({
      min: minDate,
      max: maxDate,
      pattern,
      title: description
    }),
    [minDate, maxDate, pattern, description]
  );
  return /* @__PURE__ */ jsx19(
    BaseInputContainer,
    {
      className: "blink-input-date",
      standalone: isStandalone,
      description: description ?? buildDefaultDateDescription({ min: minDate, max: maxDate }),
      leftAdornment: /* @__PURE__ */ jsx19("label", { htmlFor: id, children: /* @__PURE__ */ jsx19(CalendarIcon_default, { className: "text-icon-primary" }) }),
      rightAdornment: button ? /* @__PURE__ */ jsx19(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx19(
        "input",
        {
          id,
          type: pattern || !touched ? "text" : type,
          placeholder: placeholderWithRequired,
          value,
          onChange: extendedChange,
          onFocus: () => setTouched(true),
          ...validationProps,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionEmailInput.tsx
import { useEffect as useEffect3, useId as useId4, useMemo as useMemo5, useState as useState3 } from "react";

// src/ui/internal/icons/EmailIcon.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
var SvgComponent3 = (props) => /* @__PURE__ */ jsx20(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: /* @__PURE__ */ jsx20(
      "path",
      {
        fill: "currentColor",
        fillRule: "evenodd",
        d: "M1.361 2.685a.764.764 0 0 0-.157.227A2.986 2.986 0 0 0 .75 4.5v7a3 3 0 0 0 3 3h8.5a3 3 0 0 0 3-3v-7c0-.583-.166-1.128-.454-1.588a.75.75 0 0 0-.157-.227A2.995 2.995 0 0 0 12.25 1.5h-8.5c-.975 0-1.841.465-2.389 1.185ZM3.75 3h8.5c.27 0 .522.071.74.195L8.815 6.817a1.25 1.25 0 0 1-1.638-.001l-4.167-3.62C3.227 3.07 3.48 3 3.75 3Zm10 1.522V11.5a1.5 1.5 0 0 1-1.5 1.5h-8.5a1.5 1.5 0 0 1-1.5-1.5V4.524l3.942 3.424a2.75 2.75 0 0 0 3.605.002l3.953-3.428Z",
        clipRule: "evenodd"
      }
    )
  }
);
var EmailIcon_default = SvgComponent3;

// src/ui/internal/inputs/ActionEmailInput.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var ActionEmailInput = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  pattern,
  min,
  max,
  description,
  required
}) => {
  const id = useId4();
  const isStandalone = !!button;
  const [value, setValue] = useState3("");
  const [isValid, setValid] = useState3(!isStandalone && !required);
  const minLength = min;
  const maxLength = max;
  useEffect3(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity();
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  const placeholderWithRequired = (placeholder || "hello@example.com") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo5(
    () => ({
      minLength,
      maxLength,
      pattern,
      title: description
    }),
    [minLength, maxLength, pattern, description]
  );
  return /* @__PURE__ */ jsx21(
    BaseInputContainer,
    {
      className: "blink-input-email",
      standalone: isStandalone,
      description: description ?? buildDefaultTextDescription({ min: minLength, max: maxLength }),
      leftAdornment: /* @__PURE__ */ jsx21("label", { htmlFor: id, children: /* @__PURE__ */ jsx21(EmailIcon_default, { className: "text-icon-primary" }) }),
      rightAdornment: button ? /* @__PURE__ */ jsx21(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx21(
        "input",
        {
          id,
          type: pattern ? "text" : "email",
          placeholder: placeholderWithRequired,
          value,
          onChange: extendedChange,
          ...validationProps,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionNumberInput.tsx
import { useEffect as useEffect4, useId as useId5, useMemo as useMemo6, useState as useState4 } from "react";

// src/ui/internal/icons/NumberIcon.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var SvgComponent4 = (props) => /* @__PURE__ */ jsx22(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    fill: "none",
    viewBox: "0 0 16 16",
    preserveAspectRatio: "xMidYMid meet",
    ...props,
    children: /* @__PURE__ */ jsx22(
      "path",
      {
        fill: "currentColor",
        d: "M11.721 1a.75.75 0 0 0-.745.664l-.299 2.586h-4.5l.28-2.42a.745.745 0 0 0-1.48-.17l-.3 2.59H1.75a.75.75 0 0 0 0 1.5H4.5l-.515 4.5H1.75a.75.75 0 0 0 0 1.5h2.063l-.28 2.42a.745.745 0 1 0 1.48.17l.3-2.59h4.5l-.28 2.42a.745.745 0 0 0 1.48.17l.3-2.59h2.937a.75.75 0 0 0 0-1.5H11.5l.52-4.5h2.23a.75.75 0 0 0 0-1.5h-2.063l.279-2.414A.75.75 0 0 0 11.721 1Zm-1.736 9.25H5.5l.52-4.5h4.48l-.515 4.5Z"
      }
    )
  }
);
var NumberIcon_default = SvgComponent4;

// src/ui/internal/inputs/ActionNumberInput.tsx
import { jsx as jsx23 } from "react/jsx-runtime";
var ActionNumberInput = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  pattern,
  min,
  max,
  description,
  required
}) => {
  const id = useId5();
  const isStandalone = !!button;
  const [value, setValue] = useState4("");
  const [isValid, setValid] = useState4(!isStandalone && !required);
  useEffect4(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity();
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  const placeholderWithRequired = (placeholder || "Type here...") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo6(
    () => ({
      min: !pattern ? min : void 0,
      max: !pattern ? max : void 0,
      pattern,
      title: description
    }),
    [min, max, pattern, description]
  );
  return /* @__PURE__ */ jsx23(
    BaseInputContainer,
    {
      className: "blink-input-number",
      standalone: isStandalone,
      description: description ?? buildDefaultNumberDescription({
        min,
        max
      }),
      leftAdornment: /* @__PURE__ */ jsx23("label", { htmlFor: id, children: /* @__PURE__ */ jsx23(NumberIcon_default, { className: "text-icon-primary" }) }),
      rightAdornment: button ? /* @__PURE__ */ jsx23(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx23(
        "input",
        {
          id,
          type: pattern ? "text" : "number",
          placeholder: placeholderWithRequired,
          value,
          step: "any",
          onChange: extendedChange,
          ...validationProps,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionRadioGroup.tsx
import clsx8 from "clsx";
import { useCallback as useCallback2, useEffect as useEffect5, useMemo as useMemo7, useState as useState5 } from "react";

// src/ui/internal/Radio.tsx
import clsx7 from "clsx";
import { useId as useId6 } from "react";
import { jsx as jsx24, jsxs as jsxs13 } from "react/jsx-runtime";
var Radio = ({
  label,
  value,
  onChange,
  name,
  disabled,
  inputValue
}) => {
  const id = useId6();
  const labelId = `${id}_label`;
  return /* @__PURE__ */ jsxs13(
    "button",
    {
      className: clsx7("flex h-full gap-2.5", {
        "cursor-pointer": !disabled,
        "cursor-not-allowed": disabled
      }),
      onClick: () => !disabled && onChange(!value),
      children: [
        /* @__PURE__ */ jsxs13("div", { className: "flex h-full items-center", children: [
          /* @__PURE__ */ jsx24(
            "input",
            {
              type: "radio",
              name,
              className: "hidden",
              defaultValue: inputValue
            }
          ),
          /* @__PURE__ */ jsx24(
            "div",
            {
              role: "radio",
              id,
              "aria-labelledby": labelId,
              className: clsx7(
                "mt-0.5 flex aspect-square h-[16px] items-center justify-center rounded-full border transition-colors motion-reduce:transition-none",
                {
                  "border-input-stroke bg-input-bg": !value && !disabled,
                  "border-input-stroke-selected bg-input-bg-selected": value && !disabled,
                  "border-input-stroke-disabled bg-input-bg": !value && disabled,
                  "border-input-stroke-disabled bg-input-bg-disabled": value && disabled
                }
              ),
              children: /* @__PURE__ */ jsx24(
                "div",
                {
                  className: clsx7("aspect-square h-[8px] rounded-full bg-input-bg", {
                    invisible: !value
                  })
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx24(
          "label",
          {
            className: "cursor-[inherit] text-text text-text-input",
            id: labelId,
            children: label
          }
        )
      ]
    }
  );
};

// src/ui/internal/inputs/ActionRadioGroup.tsx
import { jsx as jsx25, jsxs as jsxs14 } from "react/jsx-runtime";
var ActionRadioGroup = ({
  placeholder: label,
  // in base inputs it's placeholder, for selectables - label
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  description,
  options = [],
  required
}) => {
  const isStandalone = !!button;
  const hasInitiallySelectedOption = useMemo7(
    () => options.find((option) => option.selected),
    [options]
  );
  const [value, setValue] = useState5(
    options.find((option) => option.selected)?.value ?? ""
  );
  const [isValid, setValid] = useState5(
    isStandalone ? !!hasInitiallySelectedOption : !(required && !hasInitiallySelectedOption)
  );
  const [touched, setTouched] = useState5(false);
  useEffect5(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = useCallback2(
    (value2) => {
      setValue(value2);
      setValid(true);
      setTouched(true);
      onChange?.(value2);
      onValidityChange?.(true);
    },
    [onChange, onValidityChange]
  );
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: clsx8("blink-input blink-input-radio-group py-1.5", {
        "bg-bg-secondary rounded-input px-1.5 pt-2": isStandalone
      }),
      children: [
        /* @__PURE__ */ jsxs14("div", { className: clsx8(isStandalone && "px-2"), children: [
          label && /* @__PURE__ */ jsx25("div", { className: "blink-input-label mb-1", children: /* @__PURE__ */ jsxs14("label", { className: "text-subtext text-text-input block font-semibold", children: [
            label,
            required && !isStandalone ? "*" : ""
          ] }) }),
          /* @__PURE__ */ jsx25(
            "div",
            {
              className: clsx8("pt-2", {
                "flex flex-col gap-3": !isStandalone,
                "grid grid-cols-2 gap-x-4 gap-y-5": isStandalone
              }),
              children: options.map((option) => /* @__PURE__ */ jsx25(
                "div",
                {
                  className: "inline-flex",
                  children: /* @__PURE__ */ jsx25(
                    Radio,
                    {
                      label: option.label,
                      value: option.value === value,
                      inputValue: option.value,
                      onChange: () => extendedChange(option.value),
                      name,
                      disabled
                    }
                  )
                },
                `${option.value}_${option.label}`
              ))
            }
          )
        ] }),
        isStandalone && /* @__PURE__ */ jsx25("div", { className: "mt-4", children: /* @__PURE__ */ jsx25(
          ActionButton,
          {
            ...button,
            onClick: () => button.onClick({ [name]: value }),
            disabled: button.disabled || !value || !isValid
          }
        ) }),
        description && /* @__PURE__ */ jsx25(
          "span",
          {
            className: clsx8(
              "blink-input-description text-caption font-medium",
              touched && !isValid ? "text-text-error" : "text-text-secondary",
              isStandalone ? "mb-2 mt-2.5" : "mt-3"
            ),
            children: /* @__PURE__ */ jsx25(SimpleMarkdown, { text: description })
          }
        )
      ]
    }
  );
};

// src/ui/internal/inputs/ActionSelect.tsx
import { useEffect as useEffect6, useId as useId7, useMemo as useMemo8, useState as useState6 } from "react";
import { jsx as jsx26, jsxs as jsxs15 } from "react/jsx-runtime";
var ActionSelect = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  description,
  required,
  options = []
}) => {
  const isStandalone = !!button;
  const placeholderValueId = useId7();
  const placeholderWithRequired = (placeholder || "Select...") + (required && !isStandalone ? "*" : "");
  const initiallySelectedOption = useMemo8(
    () => options.find((option) => option.selected),
    [options]
  );
  const [value, setValue] = useState6(
    initiallySelectedOption?.value ?? placeholderValueId
  );
  const [isValid, setValid] = useState6(
    isStandalone ? !!initiallySelectedOption : !(required && !initiallySelectedOption)
  );
  useEffect6(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity() && placeholderValueId !== value2;
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  return /* @__PURE__ */ jsx26(
    BaseInputContainer,
    {
      className: "blink-input-select",
      standalone: isStandalone,
      description,
      rightAdornment: button ? /* @__PURE__ */ jsx26(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsxs15(
        "select",
        {
          className: "mr-1.5 cursor-pointer",
          value,
          onChange: extendedChange,
          required: button ? true : required,
          disabled,
          children: [
            /* @__PURE__ */ jsx26("option", { disabled: true, value: placeholderValueId, children: placeholderWithRequired }),
            options.map((option) => /* @__PURE__ */ jsx26("option", { value: option.value, children: option.label }, `${option.value}_${option.label}`))
          ]
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionTextArea.tsx
import { useEffect as useEffect7, useMemo as useMemo9, useState as useState7 } from "react";
import { jsx as jsx27 } from "react/jsx-runtime";
var ActionTextArea = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  min,
  max,
  description,
  required
}) => {
  const isStandalone = !!button;
  const [value, setValue] = useState7("");
  const [isValid, setValid] = useState7(!isStandalone && !required);
  const minLength = min;
  const maxLength = max;
  useEffect7(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity();
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  const placeholderWithRequired = (placeholder || "Type here...") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo9(
    () => ({
      minLength,
      maxLength,
      title: description
    }),
    [minLength, maxLength, description]
  );
  return /* @__PURE__ */ jsx27(
    BaseInputContainer,
    {
      className: "blink-input-textarea",
      standalone: false,
      description: description ?? buildDefaultTextDescription({ min: minLength, max: maxLength }),
      footer: button ? /* @__PURE__ */ jsx27(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx27(
        "textarea",
        {
          className: "my-1.5 whitespace-normal",
          placeholder: placeholderWithRequired,
          value,
          onChange: extendedChange,
          ...validationProps,
          rows: 3,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionTextInput.tsx
import { useEffect as useEffect8, useMemo as useMemo10, useState as useState8 } from "react";
import { jsx as jsx28 } from "react/jsx-runtime";
var ActionTextInput = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  pattern,
  min,
  max,
  description,
  required
}) => {
  const isStandalone = !!button;
  const [value, setValue] = useState8("");
  const [isValid, setValid] = useState8(!isStandalone && !required);
  const minLength = min;
  const maxLength = max;
  useEffect8(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity();
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  const placeholderWithRequired = (placeholder || "Type here...") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo10(
    () => ({
      minLength,
      maxLength,
      pattern,
      title: description
    }),
    [minLength, maxLength, pattern, description]
  );
  return /* @__PURE__ */ jsx28(
    BaseInputContainer,
    {
      className: "blink-input-text",
      standalone: isStandalone,
      description: description ?? buildDefaultTextDescription({ min: minLength, max: maxLength }),
      rightAdornment: button ? /* @__PURE__ */ jsx28(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx28(
        "input",
        {
          type: "text",
          placeholder: placeholderWithRequired,
          value,
          onChange: extendedChange,
          ...validationProps,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/internal/inputs/ActionUrlInput.tsx
import { useEffect as useEffect9, useId as useId8, useMemo as useMemo11, useState as useState9 } from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
var ActionUrlInput = ({
  placeholder,
  name,
  button,
  disabled,
  onChange,
  onValidityChange,
  pattern,
  min,
  max,
  description,
  required
}) => {
  const isStandalone = !!button;
  const id = useId8();
  const [value, setValue] = useState9("");
  const [isValid, setValid] = useState9(!isStandalone && !required);
  const minLength = min;
  const maxLength = max;
  useEffect9(() => {
    onValidityChange?.(isValid);
  }, []);
  const extendedChange = (e) => {
    const value2 = e.currentTarget.value;
    const validity = e.currentTarget.checkValidity();
    setValue(value2);
    setValid(validity);
    onChange?.(value2);
    onValidityChange?.(validity);
  };
  const placeholderWithRequired = (placeholder || "https://") + (required && !isStandalone ? "*" : "");
  const validationProps = useMemo11(
    () => ({
      minLength,
      maxLength,
      pattern,
      title: description
    }),
    [minLength, maxLength, pattern, description]
  );
  return /* @__PURE__ */ jsx29(
    BaseInputContainer,
    {
      className: "blink-input-url",
      standalone: isStandalone,
      description: description ?? buildDefaultTextDescription({ min: minLength, max: maxLength }),
      leftAdornment: /* @__PURE__ */ jsx29("label", { htmlFor: id, children: /* @__PURE__ */ jsx29(LinkIcon, { className: "text-icon-primary" }) }),
      rightAdornment: button ? /* @__PURE__ */ jsx29(
        ActionButton,
        {
          ...button,
          onClick: () => button.onClick({ [name]: value }),
          disabled: button.disabled || value === "" || !isValid
        }
      ) : null,
      children: /* @__PURE__ */ jsx29(
        "input",
        {
          id,
          type: pattern ? "text" : "url",
          placeholder: placeholderWithRequired,
          value,
          onChange: extendedChange,
          ...validationProps,
          required: button ? true : required,
          disabled
        }
      )
    }
  );
};

// src/ui/layouts/presets.ts
var themeClassMap = {
  default: "dial-light",
  "x-dark": "x-dark",
  "x-light": "x-light",
  custom: "custom"
};

// src/ui/layouts/BaseBlinkLayout.tsx
import { Fragment as Fragment2, jsx as jsx30, jsxs as jsxs16 } from "react/jsx-runtime";
var Linkable = ({
  url,
  className,
  children
}) => url ? /* @__PURE__ */ jsx30(
  "a",
  {
    href: url,
    target: "_blank",
    rel: "noopener noreferrer",
    className,
    children
  }
) : /* @__PURE__ */ jsx30("div", { className, children });
var NotSupportedBlock = ({
  message,
  className
}) => {
  return /* @__PURE__ */ jsx30("div", { className: clsx9("blink-not-supported-block", className), children: /* @__PURE__ */ jsx30(
    "div",
    {
      className: clsx9(
        "bg-bg-secondary text-subtext text-text-secondary rounded-xl border border-none p-3"
      ),
      children: /* @__PURE__ */ jsxs16("div", { className: "flex flex-row gap-2", children: [
        /* @__PURE__ */ jsx30("div", { children: /* @__PURE__ */ jsx30(ConfigIcon, { className: "text-icon-primary" }) }),
        /* @__PURE__ */ jsxs16("div", { className: "flex flex-col justify-center gap-[3px]", children: [
          /* @__PURE__ */ jsx30("a", { className: "font-semibold", children: "This action is not supported" }),
          /* @__PURE__ */ jsx30("p", { children: message })
        ] })
      ] })
    }
  ) });
};
var DisclaimerBlock = ({
  type,
  hidden,
  ignorable,
  onSkip,
  className
}) => {
  if (type === DisclaimerType.BLOCKED && !hidden) {
    return /* @__PURE__ */ jsx30("div", { className, children: /* @__PURE__ */ jsxs16(Snackbar, { variant: "error", children: [
      /* @__PURE__ */ jsxs16("p", { children: [
        "This Action or it's origin has been flagged as an unsafe action, & has been blocked. If you believe this action has been blocked in error, please",
        " ",
        /* @__PURE__ */ jsx30(
          "a",
          {
            href: "https://discord.gg/saydialect",
            className: "cursor-pointer underline",
            target: "_blank",
            rel: "noopener noreferrer",
            children: "submit an issue"
          }
        ),
        ".",
        !ignorable && " Your action provider blocks execution of this action."
      ] }),
      ignorable && onSkip && /* @__PURE__ */ jsx30(
        "button",
        {
          className: "hover:text-text-error-hover mt-3 font-semibold transition-colors motion-reduce:transition-none",
          onClick: onSkip,
          children: "Ignore warning & proceed"
        }
      )
    ] }) });
  }
  if (type === DisclaimerType.UNKNOWN) {
    return /* @__PURE__ */ jsx30("div", { className, children: /* @__PURE__ */ jsxs16(Snackbar, { variant: "warning", children: [
      /* @__PURE__ */ jsxs16("p", { children: [
        "This Action has not yet been registered. Only use it if you trust the source. This Action will not unfurl on X until it is registered.",
        !ignorable && " Your action provider blocks execution of this action."
      ] }),
      /* @__PURE__ */ jsx30(
        "a",
        {
          className: "hover:text-text-warning-hover mt-3 inline-block font-semibold transition-colors motion-reduce:transition-none",
          href: "https://discord.gg/saydialect",
          target: "_blank",
          rel: "noopener noreferrer",
          children: "Report"
        }
      )
    ] }) });
  }
  return null;
};
var BaseBlinkLayout = ({
  stylePreset = "default",
  title,
  description,
  image,
  websiteUrl,
  websiteText,
  securityState,
  disclaimer,
  buttons,
  inputs,
  form,
  error,
  success,
  message,
  supportability,
  id,
  shouldShowWebsiteLink
}) => {
  return /* @__PURE__ */ jsx30("div", { className: clsx9("blink", themeClassMap[stylePreset]), children: /* @__PURE__ */ jsxs16("div", { className: "border-stroke-primary bg-bg-primary shadow-action w-full cursor-default overflow-hidden rounded-2xl border", children: [
    image && /* @__PURE__ */ jsx30(
      Linkable,
      {
        url: websiteUrl,
        className: "blink-image px-padding pt-padding block max-h-[100cqw] overflow-y-hidden",
        children: /* @__PURE__ */ jsx30(
          "img",
          {
            className: clsx9(
              "aspect-auto w-full rounded-xl object-cover object-center"
            ),
            src: image,
            alt: "action-image"
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs16("div", { className: "px-padding pb-padding pt-gap flex flex-col", children: [
      shouldShowWebsiteLink ? /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-2", children: [
        websiteUrl && /* @__PURE__ */ jsxs16(
          "a",
          {
            href: websiteUrl,
            target: "_blank",
            className: "blink-provider-url text-subtext group flex min-w-0 items-center gap-2 hover:cursor-pointer",
            rel: "noopener noreferrer",
            children: [
              /* @__PURE__ */ jsx30(LinkIcon, { className: "text-icon-primary group-hover:text-icon-primary-hover h-4 min-w-4 transition-colors motion-reduce:transition-none" }),
              /* @__PURE__ */ jsx30("span", { className: "text-text-link group-hover:text-text-link-hover mt-[1px] block min-w-0 truncate transition-colors group-hover:underline motion-reduce:transition-none", children: websiteText ?? websiteUrl })
            ]
          }
        ),
        websiteText && !websiteUrl && /* @__PURE__ */ jsx30("span", { className: "blink-provider-url text-subtext text-text-link min-w-0 truncate", children: websiteText }),
        /* @__PURE__ */ jsxs16(
          "a",
          {
            href: "https://docs.dialect.to/documentation/actions/security",
            target: "_blank",
            rel: "noopener noreferrer",
            className: "blink-security-badge mb-0.5 flex items-center",
            children: [
              securityState === "malicious" && /* @__PURE__ */ jsx30(
                Badge,
                {
                  variant: "error",
                  icon: /* @__PURE__ */ jsx30(ExclamationShieldIcon, { width: 13, height: 13 }),
                  children: "Blocked"
                }
              ),
              securityState === "trusted" && /* @__PURE__ */ jsx30(
                Badge,
                {
                  variant: "default",
                  icon: /* @__PURE__ */ jsx30(InfoShieldIcon, { width: 13, height: 13 })
                }
              ),
              securityState === "unknown" && /* @__PURE__ */ jsx30(
                Badge,
                {
                  variant: "warning",
                  icon: /* @__PURE__ */ jsx30(InfoShieldIcon, { width: 13, height: 13 })
                }
              )
            ]
          }
        )
      ] }) : null,
      /* @__PURE__ */ jsx30("span", { className: "blink-title text-text text-text-primary mb-1 mt-1.5 break-words font-semibold", children: title }),
      /* @__PURE__ */ jsx30("span", { className: "blink-description text-subtext text-text-secondary mb-gap break-words", children: description && /* @__PURE__ */ jsx30(SimpleMarkdown, { text: description }) }),
      !supportability.isSupported ? /* @__PURE__ */ jsx30(NotSupportedBlock, { message: supportability.message }) : /* @__PURE__ */ jsxs16(Fragment2, { children: [
        disclaimer && /* @__PURE__ */ jsx30(
          DisclaimerBlock,
          {
            className: "blink-disclaimer-block mb-gap",
            type: disclaimer.type,
            ignorable: disclaimer.ignorable,
            hidden: disclaimer.type === DisclaimerType.BLOCKED ? disclaimer.hidden : false,
            onSkip: disclaimer.type === DisclaimerType.BLOCKED ? disclaimer.onSkip : void 0
          }
        ),
        /* @__PURE__ */ jsx30(
          ActionContent,
          {
            form,
            inputs,
            buttons
          },
          id
        ),
        success && /* @__PURE__ */ jsx30("span", { className: "blink-message-success text-subtext text-text-success mt-between-inputs break-words text-center", children: success }),
        error && !success && /* @__PURE__ */ jsx30("span", { className: "blink-message-error text-subtext text-text-error mt-between-inputs break-words text-center", children: error }),
        message && !success && !error && /* @__PURE__ */ jsx30("span", { className: "blink-message-default text-subtext text-text-secondary mt-between-inputs break-words text-center", children: message })
      ] }),
      /* @__PURE__ */ jsx30("div", { className: "blink-powered-by mt-4 flex justify-center", children: /* @__PURE__ */ jsxs16(
        "a",
        {
          href: "https://dialect.to",
          target: "_blank",
          className: "text-subtext text-text-link hover:text-text-link-hover flex items-center gap-1 transition-colors hover:cursor-pointer motion-reduce:transition-none",
          rel: "noreferrer",
          children: [
            /* @__PURE__ */ jsx30("span", { children: "powered by" }),
            /* @__PURE__ */ jsx30(DialectLogo, {})
          ]
        }
      ) })
    ] })
  ] }) });
};
var ActionContent = ({
  form,
  inputs,
  buttons
}) => {
  if (form) {
    return /* @__PURE__ */ jsx30(ActionForm, { form });
  }
  return /* @__PURE__ */ jsxs16("div", { className: "gap-between-inputs flex flex-col", children: [
    buttons && buttons.length > 0 && /* @__PURE__ */ jsx30("div", { className: "gap-between-buttons flex flex-wrap items-center", children: buttons?.map((it, index) => /* @__PURE__ */ jsx30(
      "div",
      {
        className: "flex flex-grow basis-[calc(33.333%-2*4px)]",
        children: /* @__PURE__ */ jsx30(ActionButton, { ...it })
      },
      index
    )) }),
    inputs?.map((input) => /* @__PURE__ */ jsx30(ActionInputFactory, { ...input }, input.name))
  ] });
};
var buildDefaultFormValues = (inputs) => {
  return Object.fromEntries(
    inputs.map((i) => {
      if (i.type === "checkbox") {
        return [
          i.name,
          i.options?.filter((o) => o.selected).map((o) => o.value)
        ];
      }
      return i.type === "radio" || i.type === "select" ? [i.name, i.options?.find((o) => o.selected)?.value] : null;
    }).filter((i) => !!i)
  );
};
var ActionForm = ({ form }) => {
  const [values, setValues] = useState10(
    buildDefaultFormValues(form.inputs)
  );
  const [validity, setValidity] = useState10(
    Object.fromEntries(form.inputs.map((i) => [i.name, false]))
  );
  const onChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };
  const onValidityChange = (name, state) => {
    setValidity((prev) => ({ ...prev, [name]: state }));
  };
  const disabled = Object.values(validity).some((v) => !v);
  return /* @__PURE__ */ jsxs16("div", { className: "gap-between-inputs flex flex-col", children: [
    form.inputs.map((input) => /* @__PURE__ */ jsx30(
      ActionInputFactory,
      {
        ...input,
        onChange: (v) => onChange(input.name, v),
        onValidityChange: (v) => onValidityChange(input.name, v)
      },
      input.name
    )),
    /* @__PURE__ */ jsx30(
      ActionButton,
      {
        ...form.button,
        onClick: () => form.button.onClick(values),
        disabled: form.button.disabled || disabled
      }
    )
  ] });
};
var ActionInputFactory = (input) => {
  switch (input.type) {
    case "checkbox":
      return /* @__PURE__ */ jsx30(ActionCheckboxGroup, { ...input });
    case "radio":
      return /* @__PURE__ */ jsx30(ActionRadioGroup, { ...input });
    case "date":
    case "datetime-local":
      return /* @__PURE__ */ jsx30(ActionDateInput, { ...input, type: input.type });
    case "select":
      return /* @__PURE__ */ jsx30(ActionSelect, { ...input });
    case "email":
      return /* @__PURE__ */ jsx30(ActionEmailInput, { ...input });
    case "number":
      return /* @__PURE__ */ jsx30(ActionNumberInput, { ...input });
    case "url":
      return /* @__PURE__ */ jsx30(ActionUrlInput, { ...input });
    case "textarea":
      return /* @__PURE__ */ jsx30(ActionTextArea, { ...input });
    default:
      return /* @__PURE__ */ jsx30(ActionTextInput, { ...input });
  }
};

// src/ui/ActionsOnlyBlink.tsx
import {
  BlinkContainer
} from "@dialectlabs/blinks-core";
import { useCallback as useCallback3 } from "react";

// src/ui/layouts/ActionsOnlyBlinkLayout.tsx
import clsx10 from "clsx";
import { jsx as jsx31, jsxs as jsxs17 } from "react/jsx-runtime";
var ActionsOnlyBlinkLayout = ({
  stylePreset = "default",
  id,
  success,
  error,
  message,
  inputs,
  buttons,
  form
}) => {
  return /* @__PURE__ */ jsxs17("div", { className: clsx10("blink w-full", themeClassMap[stylePreset]), children: [
    /* @__PURE__ */ jsx31(ActionContent, { form, inputs, buttons }, id),
    success && /* @__PURE__ */ jsx31("span", { className: "blink-message-success text-subtext text-text-success mt-between-inputs break-words text-center", children: success }),
    error && !success && /* @__PURE__ */ jsx31("span", { className: "blink-message-error text-subtext text-text-error mt-between-inputs break-words text-center", children: error }),
    message && !success && !error && /* @__PURE__ */ jsx31("span", { className: "blink-message-default text-subtext text-text-secondary mt-between-inputs break-words text-center", children: message })
  ] });
};

// src/ui/ActionsOnlyBlink.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var ActionsOnlyBlink = ({
  _Layout: Layout = NormalizedActionsOnlyBlinkLayout,
  stylePreset,
  ...props
}) => {
  const LayoutWithPreset = useCallback3(
    (props2) => /* @__PURE__ */ jsx32(Layout, { ...props2, stylePreset }),
    [Layout, stylePreset]
  );
  return /* @__PURE__ */ jsx32(
    BlinkContainer,
    {
      ...props,
      selector: void 0,
      Layout: LayoutWithPreset
    }
  );
};
var NormalizedActionsOnlyBlinkLayout = (props) => {
  const normalizedProps = useBaseLayoutPropNormalizer(props);
  return /* @__PURE__ */ jsx32(ActionsOnlyBlinkLayout, { ...normalizedProps });
};

// src/ui/BlinkComponent.tsx
import {
  BlinkContainer as BlinkContainer2
} from "@dialectlabs/blinks-core";
import { useCallback as useCallback4 } from "react";
import { jsx as jsx33 } from "react/jsx-runtime";
var BlinkComponent = ({
  _Layout: Layout = NormalizedBaseBlinkLayout,
  stylePreset,
  ...props
}) => {
  const LayoutWithPreset = useCallback4(
    (props2) => /* @__PURE__ */ jsx33(Layout, { ...props2, stylePreset }),
    [Layout, stylePreset]
  );
  return /* @__PURE__ */ jsx33(
    BlinkContainer2,
    {
      ...props,
      selector: void 0,
      Layout: LayoutWithPreset
    }
  );
};
var NormalizedBaseBlinkLayout = (props) => {
  const normalizedProps = useBaseLayoutPropNormalizer(props);
  return /* @__PURE__ */ jsx33(BaseBlinkLayout, { ...normalizedProps });
};

// src/ui/Miniblink.tsx
import {
  BlinkContainer as BlinkContainer3
} from "@dialectlabs/blinks-core";
import { useCallback as useCallback6 } from "react";

// src/ui/hooks/useIsolatedLayoutPropNormalizer.ts
import {
  ButtonActionComponent as ButtonActionComponent2,
  FormActionComponent as FormActionComponent2,
  isParameterSelectable as isParameterSelectable2,
  isPatternAllowed as isPatternAllowed2,
  MultiValueActionComponent as MultiValueActionComponent2,
  SingleValueActionComponent as SingleValueActionComponent2
} from "@dialectlabs/blinks-core";
import { useCallback as useCallback5, useMemo as useMemo12 } from "react";
var SOFT_LIMIT_FORM_INPUTS2 = 10;
var useIsolatedLayoutPropNormalizer = ({
  executeFn,
  executionStatus,
  executingAction,
  blink,
  component,
  caption,
  ...props
}) => {
  const asButtonProps = useCallback5(
    (it) => {
      return {
        text: buttonLabelMap[executionStatus] ?? it.label,
        loading: executionStatus === "executing" && (it === executingAction || it.parentComponent === executingAction),
        disabled: blink.disabled || blink.type === "completed" || executionStatus !== "idle",
        variant: buttonVariantMap[blink.type === "completed" ? "success" : executionStatus],
        ctaType: (it.type === "external-link" || it.type === "inline-link") && (executionStatus === "idle" || executionStatus === "blocked") ? "link" : "button",
        onClick: async (params) => {
          const extra = await executeFn(it.parentComponent ?? it, params);
          if (!extra) {
            return;
          }
          if (extra.type === "external-link") {
            const result = window.confirm(
              `This action redirects to another website: ${extra.data.externalLink}, the link will open in a new tab of your browser`
            );
            if (result) {
              window.open(
                extra.data.externalLink,
                "_blank",
                "norefferer,noopener"
              );
              return extra.onNext();
            }
            return extra.onCancel?.();
          }
        }
      };
    },
    [blink.disabled, blink.type, executeFn, executingAction, executionStatus]
  );
  const asInputProps = useCallback5(
    (it, { placement } = {
      placement: "standalone"
    }) => {
      return {
        type: it.parameter.type ?? "text",
        placeholder: it.parameter.label,
        disabled: blink.disabled || blink.type === "completed" || executionStatus !== "idle",
        name: it.parameter.name,
        required: it.parameter.required,
        min: it.parameter.min,
        max: it.parameter.max,
        pattern: it instanceof SingleValueActionComponent2 && isPatternAllowed2(it.parameter) ? it.parameter.pattern : void 0,
        options: isParameterSelectable2(it.parameter) ? it.parameter.options : void 0,
        description: it.parameter.patternDescription,
        button: placement === "standalone" ? asButtonProps(it.toButtonActionComponent()) : void 0
      };
    },
    [blink.disabled, blink.type, asButtonProps, executionStatus]
  );
  const asFormProps = useCallback5(
    (it) => {
      return {
        button: asButtonProps(it.toButtonActionComponent()),
        inputs: it.parameters.toSpliced(SOFT_LIMIT_FORM_INPUTS2).map(
          (parameter) => asInputProps(it.toInputActionComponent(parameter.name), {
            placement: "form"
          })
        )
      };
    },
    [asButtonProps, asInputProps]
  );
  const elementProps = useMemo12(() => {
    if (!component) {
      return null;
    }
    if (component instanceof SingleValueActionComponent2 || component instanceof MultiValueActionComponent2) {
      return {
        elementType: "input",
        element: asInputProps(component)
      };
    }
    if (component instanceof FormActionComponent2) {
      return { elementType: "form", element: asFormProps(component) };
    }
    if (component instanceof ButtonActionComponent2) {
      return {
        elementType: "button",
        element: asButtonProps(component)
      };
    }
    return null;
  }, [asButtonProps, asFormProps, asInputProps, component]);
  const normalizedCaption = useMemo12(() => {
    if (!caption) {
      return {};
    }
    if (caption.type === "success") {
      return {
        success: caption.text
      };
    }
    if (caption.type === "error") {
      return {
        error: caption.text
      };
    }
    return {};
  }, [caption]);
  if (!elementProps) {
    return null;
  }
  return {
    ...props,
    websiteText: (props.websiteText === false ? "" : props.websiteText) ?? props.websiteUrl ?? blink.url,
    ...elementProps,
    ...normalizedCaption
  };
};

// src/ui/layouts/IsolatedBlinkLayout.tsx
import clsx11 from "clsx";
import { useMemo as useMemo13 } from "react";
import { jsx as jsx34, jsxs as jsxs18 } from "react/jsx-runtime";
var IsolatedBlinkLayout = ({
  stylePreset = "default",
  id,
  success,
  error,
  message,
  ...props
}) => {
  const element = useMemo13(() => {
    if (props.elementType === "form") {
      return { form: props.element };
    }
    if (props.elementType === "button") {
      return { buttons: [props.element] };
    }
    if (props.elementType === "input") {
      return { inputs: [props.element] };
    }
    return {};
  }, [props.element, props.elementType]);
  return /* @__PURE__ */ jsxs18("div", { className: clsx11("blink w-full", themeClassMap[stylePreset]), children: [
    /* @__PURE__ */ jsx34(ActionContent, { ...element }, id),
    success && /* @__PURE__ */ jsx34("span", { className: "blink-message-success text-subtext text-text-success mt-1.5 break-words text-center", children: success }),
    error && !success && /* @__PURE__ */ jsx34("span", { className: "blink-message-error text-subtext text-text-error mt-1.5 break-words text-center", children: error }),
    message && !success && !error && /* @__PURE__ */ jsx34("span", { className: "blink-message-default text-subtext text-text-secondary mt-1.5 break-words text-center", children: message })
  ] });
};

// src/ui/Miniblink.tsx
import { jsx as jsx35 } from "react/jsx-runtime";
var Miniblink = ({
  _Layout: Layout = NormalizedIsolatedBlinkLayout,
  stylePreset,
  ...props
}) => {
  const LayoutWithPreset = useCallback6(
    (props2) => /* @__PURE__ */ jsx35(Layout, { ...props2, stylePreset }),
    [Layout, stylePreset]
  );
  return /* @__PURE__ */ jsx35(BlinkContainer3, { ...props, securityLevel: "all", Layout: LayoutWithPreset });
};
var NormalizedIsolatedBlinkLayout = (props) => {
  const normalizedProps = useIsolatedLayoutPropNormalizer(props);
  if (!normalizedProps) {
    console.warn(
      "[@dialectlabs/blinks] No `selector` prop provided for Miniblink"
    );
    return null;
  }
  return /* @__PURE__ */ jsx35(IsolatedBlinkLayout, { ...normalizedProps });
};

export {
  useBaseLayoutPropNormalizer,
  BaseBlinkLayout,
  ActionContent,
  ActionsOnlyBlink,
  NormalizedActionsOnlyBlinkLayout,
  BlinkComponent,
  NormalizedBaseBlinkLayout,
  Miniblink,
  NormalizedIsolatedBlinkLayout
};
